/**
 * Compact app-runtime resolver for airport-local flight times.
 *
 * Keep this intentionally small so mobile does not bundle a full airport
 * database. The Spring DD workbook audit compares every real flight airport
 * in `data/workbooks/event-spring-dd-frg-naples.xlsx` against `airport-utils`
 * (OPTD-backed, dev-only) so new real airport codes are deliberate additions.
 */
const AIRPORT_TIMEZONES: Record<string, string> = {
  ALB: 'America/New_York',
  ATL: 'America/New_York',
  CHS: 'America/New_York',
  CLT: 'America/New_York',
  DEN: 'America/Denver',
  DFW: 'America/Chicago',
  DTW: 'America/Detroit',
  GRR: 'America/Detroit',
  IAH: 'America/Chicago',
  IND: 'America/Indiana/Indianapolis',
  JAC: 'America/Denver',
  JFK: 'America/New_York',
  LAX: 'America/Los_Angeles',
  LGA: 'America/New_York',
  MEM: 'America/Chicago',
  MIA: 'America/New_York',
  ORD: 'America/Chicago',
  PBI: 'America/New_York',
  PHL: 'America/New_York',
  RSW: 'America/New_York',
  SEA: 'America/Los_Angeles',
  SFO: 'America/Los_Angeles',
  SLC: 'America/Denver',
};

export type AirportTimezoneEntry = {
  code: string;
  timezone: string;
};

export const AIRPORT_TIMEZONE_ENTRIES: readonly AirportTimezoneEntry[] =
  Object.freeze(
    Object.entries(AIRPORT_TIMEZONES).map(([code, timezone]) => ({
      code,
      timezone,
    })),
  );

export function airportTimezone(airportCode: string | null | undefined): string | null {
  if (!airportCode) return null;
  const normalized = airportCode.trim().toUpperCase();
  return AIRPORT_TIMEZONES[normalized] ?? null;
}

export function requireAirportTimezone(
  airportCode: string | null | undefined,
  fieldLabel = 'airport',
): string {
  const normalized = airportCode?.trim().toUpperCase() ?? '';
  const timezone = airportTimezone(normalized);
  if (!timezone) {
    throw new Error(
      `Unsupported ${fieldLabel} airport timezone${normalized ? ` for ${normalized}` : ''}`,
    );
  }
  return timezone;
}
